import { Buffer } from "buffer"
import { get } from "http"
import { getSettings } from "./quadriSettingsApi"
import { TaskTypeDefinitionsCache } from "./TaskTypeDefinitionsCache"

export type Project = {
  organizationId: string
  organizationName: string
  connectId: string
  projectId: string
  projectName: string
}

export type Header = {
  taskId: string
  name: string
  description: string
  taskTypeTag: string
  taskClass: string
  hasChildren: boolean
  creationDate?: string | null
  lastModifiedDate?: string | null
}

export type DetailsType = {
  id: string
  name: string
  description: string
  iconSvg: string
}

export type Details = {
  type: DetailsType
  properties: TaskTypeProperty[]
}

export type TaskTypePropertyDefinition = {
  name: string
  caption: string
  description: string
  type: string
  isBrowsable: boolean
  includeInTemplate: boolean
}

export type TaskTypeProperty = {
  definition: TaskTypePropertyDefinition
  value: any
}

export type TaskType = {
  header: Header
  details: Details
  children: TaskType[]
  subTasks: TaskTypeSimple[]
}

export type TaskTypeSimple = {
  taskId: string
  name: string
  description: string
  taskTypeTag: string
  taskClass: string
  creationDate: string
  hasChildren: boolean
}

export type RawQuery = {
  name: string
  selectedFeatures: SelectedFeatureTypeSimple[]
}

export type SelectedFeatureTypeSimple = {
  featureTypeId: string
  featureIds: string[]
}

export type Query = {
  selectedFeatureTypes: SelectedFeatureType[]
}

export type SelectedFeatureType = {
  featureTypeId: string
  selectAll: boolean
  selectedFeatures: SelectedFeature[]
  selectedAttributeTypes: SelectedAttributeType[]
}

export type SelectedFeature = {
  featureId: string
  versionId: string
}

export type SelectedAttributeType = {
  attributeTypeId: string
  exlude: boolean
}

export type SearchStatus = {
  id: string
  status: string
  errorDescription: string
  downloadUrl: string
}

export type TaskTypeDefinition = {
  id: string
  name: string
  description: string
  iconSvg: string
}

export class QuadriRestApi {
  private baseUrl: string
  private accessToken: string // Static variable to store the authHeader

  constructor() {
    this.baseUrl = ""
    this.accessToken = ""
  }

  async initialize(accessToken: string, projectLocation: string) {
    const settings = await getSettings(accessToken, projectLocation)
    if (settings) {
      this.baseUrl = settings.quadriApi?.uri
    } else {
      throw new Error("Settings could not be retrieved.")
    }
    this.accessToken = accessToken
    console.log(`Using rest api url: ${this.baseUrl}`)
  }

  private requestInit(method?: string): RequestInit {
    return {
      method: method || "GET",
      mode: "cors",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.accessToken}`
      }
    }
  }

  public static encodeTaskId(projectId: string, taskId: string): string {
    // Regular expression to check if taskId is already a 8-character hexadecimal string
    const hexRegex = new RegExp(`^${projectId}-[0-9a-fA-F]{8}$`)
    if (hexRegex.test(taskId)) return taskId

    const taskIdNumber = parseInt(taskId, 10)
    const taskIdHex = taskIdNumber.toString(16)
    const paddedTaskIdHex = taskIdHex.padStart(8, "0")
    return `${projectId}-${paddedTaskIdHex}`
  }

  public static extractTaskId(encodedString: string): string {
    const parts = encodedString.split("-")
    if (parts.length < 2) {
      throw new Error("Invalid encoded string format.")
    }
    const taskIdHex = parts[parts.length - 1]
    const taskIdNumber = parseInt(taskIdHex, 16)
    return taskIdNumber.toString()
  }

  public async getProjects(): Promise<Project[]> {
    const init = this.requestInit()

    const response = await fetch(`${this.baseUrl}/api/v1/Projects`, init)
    if (!response.ok) {
      console.log("Failed to fetch projects:", response)
      throw new Error(`Failed to fetch projects: ${response.statusText}`)
    }
    const projects = await response.json()
    return projects
  }

  public async getTasks(projectId: string, tags: string[]): Promise<TaskTypeSimple[]> {
    const init = this.requestInit()
    let allItems: TaskTypeSimple[] = []

    try {
      let response
      let url = ""
      if (tags.length === 0) {
        url = `${this.baseUrl}/api/v1/projects/${projectId}/tasks`
      } else {
        url = `${this.baseUrl}/api/v1/projects/${projectId}/tasks?taskTypeTags=${tags.join(",")}`
      }

      while (url) {
        const response = await fetch(url, init)

        if (!response.ok) {
          // Handle HTTP errors
          console.error(`Error fetching tasks: ${response.status} ${response.statusText}`)
          return [] // Return an empty array in case of an error
        }

        const data = await response.json()
        if (Array.isArray(data.items)) allItems = allItems.concat(data.items)

        url = data.links?.next?.href ? `${this.baseUrl}${data.links.next.href}` : ""
      }
      return allItems
    } catch (error) {
      console.error("Error fetching tasks:", error)
      return []
    }
  }

  public async getTaskTree(projectId: string, depth: number = 3): Promise<TaskType> {
    const init = this.requestInit()

    const taskType = await fetch(`${this.baseUrl}/api/v1/projects/${projectId}/tasks/tree?depth=${depth}`, init).then(
      (res) => res.json()
    )
    return taskType
  }

  public async getTask(projectId: string, taskId: string): Promise<TaskType> {
    const init = this.requestInit()

    const taskType = await fetch(`${this.baseUrl}/api/v1/projects/${projectId}/tasks/${taskId}`, init).then((res) =>
      res.json()
    )
    return taskType
  }

  public async getTaskSchemas(): Promise<TaskTypeDefinition[]> {
    const init = this.requestInit()
    let allItems: TaskTypeDefinition[] = []
    let url = `${this.baseUrl}/api/v1/taskSchemas`

    while (url) {
      const response = await fetch(url, init)
      if (!response.ok) throw new Error(`Failed to fetch task schemas: ${response.statusText}`)

      const data = await response.json()
      if (Array.isArray(data.items)) allItems = allItems.concat(data.items)

      url = data.links?.next?.href ? `${this.baseUrl}${data.links.next.href}` : ""
    }
    return allItems
  }

  public async getTaskSchema(id: string): Promise<TaskTypeDefinition> {
    let taskTypeDefinition = TaskTypeDefinitionsCache.getInstance().getTaskTypeDefinition(id)
    if (taskTypeDefinition) return taskTypeDefinition

    const init = this.requestInit()
    const taskTypeDef = await fetch(`${this.baseUrl}/api/v1/taskSchemas/${id}`, init).then((res) => res.json())

    TaskTypeDefinitionsCache.getInstance().setTaskTypeDefinition(id, taskTypeDef)
    return taskTypeDef
  }

  public async getSubTaskTree(projectId: string, parentTaskId: string, depth: number = 3): Promise<TaskType> {
    const init = this.requestInit()

    const taskType = await fetch(
      `${this.baseUrl}/api/v1/projects/${projectId}/tasks/${parentTaskId}/tree?depth=${depth}`,
      init
    ).then((res) => res.json())
    return taskType
  }
}

export default QuadriRestApi
